import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const Ctx = createContext(null);

// One <audio> for the whole app: the set keeps playing across /music pages (and anywhere else) until paused.
export function MusicPlayerProvider({ children }) {
  const audio = useRef(null);
  const location = useLocation();
  const [songs, setSongs] = useState(null);
  const [index, setIndex] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const counted = useRef(new Set());

  useEffect(() => {
    if (songs === null && location.pathname.startsWith("/music")) {
      api.get("/music").then(({ data }) => setSongs(data)).catch(() => setSongs([]));
    }
  }, [location.pathname, songs]);

  useEffect(() => {
    const a = audio.current;
    const onPlay = () => setPlaying(true), onPause = () => setPlaying(false), onTime = () => setTime(a.currentTime);
    a.addEventListener("play", onPlay); a.addEventListener("pause", onPause); a.addEventListener("timeupdate", onTime);
    return () => { a.removeEventListener("play", onPlay); a.removeEventListener("pause", onPause); a.removeEventListener("timeupdate", onTime); };
  }, []);

  const value = useMemo(() => {
    const list = songs || [];
    const startAt = (i) => {
      const s = list[i];
      if (!s) return;
      if (i !== index) { audio.current.src = `${API}/music/${s.id}/stream`; setIndex(i); setTime(0); }
      audio.current.play().catch(() => {});
      if (!counted.current.has(s.id)) { counted.current.add(s.id); api.post(`/music/${s.id}/play`).catch(() => {}); }
    };
    const indexOf = (song) => list.findIndex((s) => s.id === song.id);
    return {
      songs, setSongs, index, playing, time, audio,
      current: index !== null ? list[index] : null,
      startAt,
      toggle: (i) => (i === index && playing ? audio.current.pause() : startAt(i)),
      playSong: (song) => {
        let i = indexOf(song);
        if (i < 0) { setSongs([song, ...list]); i = 0; audio.current.src = `${API}/music/${song.id}/stream`; setIndex(0); setTime(0); audio.current.play().catch(() => {}); return; }
        i === index && playing ? audio.current.pause() : startAt(i);
      },
      isActive: (song) => !!song && index !== null && list[index]?.id === song.id,
      playList: (newList, i = 0) => {
        const s = newList[i];
        if (!s) return;
        const same = newList.length === list.length && newList.every((x, k) => x.id === list[k]?.id);
        if (!same) setSongs(newList);
        if (!same || i !== index) { audio.current.src = `${API}/music/${s.id}/stream`; setIndex(i); setTime(0); }
        if (same && i === index && playing) { audio.current.pause(); return; }
        audio.current.play().catch(() => {});
        if (!counted.current.has(s.id)) { counted.current.add(s.id); api.post(`/music/${s.id}/play`).catch(() => {}); }
      },
      next: () => (index !== null && index + 1 < list.length ? startAt(index + 1) : audio.current.pause()),
      prev: () => startAt(Math.max(0, (index ?? 0) - 1)),
      seek: (t) => { if (audio.current.src) { audio.current.currentTime = t; audio.current.play().catch(() => {}); } },
      pause: () => audio.current.pause(),
    };
  }, [songs, index, playing, time]);

  return (
    <Ctx.Provider value={value}>
      <audio ref={audio} preload="none" onEnded={() => value.next()} data-testid="queue-audio" />
      {children}
    </Ctx.Provider>
  );
}

export const useMusicPlayer = () => useContext(Ctx);
